import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Checkbox,
  Box,
  FormControlLabel,
  Grid,
} from '@mui/material';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import heic2any from 'heic2any';
import { useRef } from 'react';
//import { useTranslation } from 'react-i18next';

const apiUrl = process.env.REACT_APP_API_URL;

const VetClinicRecordOutside = () => {
  const navigate = useNavigate();
  const chipSearchTimeoutRef = useRef(null);
  const getCurrentDate = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const hours = String(today.getHours()).padStart(2, '0');
    const minutes = String(today.getMinutes()).padStart(2, '0');

    return `${yyyy}-${mm}-${dd}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    record_date: getCurrentDate(),
    horse_id: '',
    rfid_chip_number: '',
    horse_name: '',
    client_id: '',
    client_name: '',
    client_phone: '',
    procedures: [],
    medications: [],
    notes: '',
    total_amount: 0,
    file_uploads: [],
    payment_type_id: '',
  });

  const [procedures, setProcedures] = useState([]);
  const [medications, setMedications] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [errors, setErrors] = useState({});

  const calculateTotalAmount = useCallback(() => {
    const proceduresTotal = formData.procedures.reduce((sum, procId) => {
      const proc = procedures.find((p) => p.procedure_id === parseInt(procId));
      return sum + (proc?.price || 0);
    }, 0);

    const medicationsTotal = formData.medications.reduce(
      (sum, med) => sum + (med.price || 0),
      0
    );

    setFormData((prevState) => ({
      ...prevState,
      total_amount: proceduresTotal + medicationsTotal,
    }));
  }, [formData.procedures, formData.medications, procedures]);

  useEffect(() => {
    calculateTotalAmount();
  }, [formData.procedures, formData.medications, calculateTotalAmount]);

  useEffect(() => {
    fetchDropdownData();
  }, []);

  const fetchDropdownData = async () => {
    try {
      const [
        proceduresResponse,
        medicationsResponse,
        paymentTypesResponse,
        horsesResponse,
      ] = await Promise.all([
        axios.get(`${apiUrl}/procedures`),
        axios.get(`${apiUrl}/medications`),
        axios.get(`${apiUrl}/payment-types`),
        axios.get(`${apiUrl}/vetclinic-horses`), // تعديل المسار لجلب بيانات الحصان
      ]);

      if (
        proceduresResponse.data &&
        medicationsResponse.data &&
        paymentTypesResponse.data &&
        horsesResponse.data
      ) {
        setProcedures(
          proceduresResponse.data.sort((a, b) =>
            a.procedure_name.localeCompare(b.procedure_name)
          )
        );
        setMedications(medicationsResponse.data);
        setPaymentTypes(paymentTypesResponse.data);
      } else {
        console.error('One or more responses returned empty data');
      }
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  };

  const fetchHorseAndClientData = async (horse_id) => {
    try {
      const response = await axios.get(
        `${apiUrl}/vetclinic-horses/${horse_id}`
      );
      const data = response.data;

      setFormData((prevState) => ({
        ...prevState,
        horse_name: data.horse_name,
        rfid_chip_number: data.rfid_chip_number,
        client_name: data.client_name,
        client_phone: data.client_phone,
        client_id: data.client_id,
      }));
    } catch (error) {
      console.error('Error fetching horse and client data:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));

    if (name === 'horse_id') {
      fetchHorseAndClientData(value);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/heic'];

    const validFiles = await Promise.all(
      files.map(async (file) => {
        if (file.type === 'image/heic') {
          try {
            const convertedBlob = await heic2any({
              blob: file,
              toType: 'image/jpeg',
            });
            return new File(
              [convertedBlob],
              file.name.replace(/.heic$/i, '.jpg'),
              {
                type: 'image/jpeg',
              }
            );
          } catch (error) {
            console.error('HEIC conversion error:', error);
            return null;
          }
        } else if (allowedTypes.includes(file.type)) {
          return file;
        }
        return null;
      })
    );

    const filteredValidFiles = validFiles.filter((file) => file !== null);

    if (filteredValidFiles.length === 0) {
      alert('Please upload valid images only (JPEG, PNG, GIF, HEIC).');
      return;
    }

    const uploadPromises = filteredValidFiles.map((file) => {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      return axios.post(`${apiUrl}/vetclinic-records/upload`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    });

    try {
      const responses = await Promise.all(uploadPromises);
      const uploadedFiles = responses.map((response) => response.data.filePath);

      setFormData((prevState) => ({
        ...prevState,
        file_uploads: [...prevState.file_uploads, ...uploadedFiles],
      }));
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Error uploading files. Please try again.');
    }
  };

  const fetchHorseByChipNumber = async (chipNumber) => {
    try {
      const response = await axios.get(
        `${apiUrl}/vetclinic-horses/rfid/${chipNumber}`
      );
      const data = response.data;

      if (data) {
        setFormData((prevState) => ({
          ...prevState,
          horse_name: data.horse_name || 'N/A',
          rfid_chip_number: data.rfid_chip_number || 'N/A',
          client_name: data.client_name || 'N/A',
          client_phone: data.client_phone || 'N/A',
          horse_id: data.horse_id,
          client_id: data.client_id,
        }));
      } else {
      }
    } catch (error) {
      console.error('Error fetching horse data:', error);
    }
  };

  const handleProcedureChange = (procedure_id) => {
    setFormData((prevState) => {
      const isChecked = prevState.procedures.includes(procedure_id);
      const updatedProcedures = isChecked
        ? prevState.procedures.filter((id) => id !== procedure_id)
        : [...prevState.procedures, procedure_id];

      setErrors((prevErrors) => ({
        ...prevErrors,
        procedures: updatedProcedures.length > 0 ? '' : prevErrors.procedures,
      }));

      return {
        ...prevState,
        procedures: updatedProcedures,
      };
    });
  };

  const handleMedicationChange = (index, field, value) => {
    setFormData((prevState) => {
      const updatedMedications = prevState.medications.map((med, i) => {
        if (i === index) {
          const updatedMed = { ...med, [field]: value };

          if (field === 'medication_id') {
            const selectedMed = medications.find(
              (m) => m.medication_id === parseInt(value)
            );
            if (selectedMed) {
              updatedMed.dosage = selectedMed.default_dosage || '';
              updatedMed.quantity = selectedMed.default_quantity || 1;
              updatedMed.unit = selectedMed.default_unit || '';
              updatedMed.price = selectedMed.price || 0;
            }
          }

          return updatedMed;
        }
        return med;
      });

      return {
        ...prevState,
        medications: updatedMedications,
      };
    });
  };

  const addMedication = () => {
    setFormData((prevState) => ({
      ...prevState,
      medications: [
        ...prevState.medications,
        { medication_id: '', dosage: '', quantity: '', unit: '', price: 0 },
      ],
    }));
  };

  const removeMedication = (index) => {
    setFormData((prevState) => ({
      ...prevState,
      medications: prevState.medications.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    if (!validateFormData()) return;

    const user_id = localStorage.getItem('userId');
    if (!user_id) {
      alert('User ID not found');
      return;
    }

    const work_place = 'Outside';
    let outside_horse_id = formData.horse_id;
    const inside_horse_id = null;

    try {
      let client_id = formData.client_id;

      if (!client_id && formData.client_name && formData.client_phone) {
        const createClientRes = await axios.post(`${apiUrl}/clients`, {
          client_name: formData.client_name,
          mobile: formData.client_phone,
          contact_info: formData.client_phone,
          address: '',
          notes: '',
        });

        client_id = createClientRes.data.client_id;
        setFormData((prev) => ({
          ...prev,
          client_id,
        }));
      }

      if (
        !outside_horse_id &&
        formData.horse_name &&
        formData.rfid_chip_number
      ) {
        const createHorseRes = await axios.post(`${apiUrl}/vetclinic-horses`, {
          horse_name: formData.horse_name,
          rfid_chip_number: formData.rfid_chip_number,
          client_id,
        });

        outside_horse_id = createHorseRes.data.horse_id;
        setFormData((prev) => ({
          ...prev,
          horse_id: outside_horse_id,
        }));
      }

      const finalOutsideHorseId = outside_horse_id || null;

      const payload = {
        ...formData,
        client_id,
        user_id,
        work_place,
        inside_horse_id,
        outside_horse_id: finalOutsideHorseId,
        procedures: formData.procedures.map((procId) => {
          const proc = procedures.find(
            (p) => p.procedure_id === parseInt(procId)
          );
          return { procedure_id: procId, price: proc ? proc.price : 0 };
        }),
        medications: formData.medications.map((med) => ({
          ...med,
          price: med.price || 0,
        })),
        file_uploads: formData.file_uploads,
      };

      delete payload.horse_id;

      await axios.post(`${apiUrl}/vetclinic-records-outside`, payload);

      alert('Record added successfully!');
      navigate('/RecordList');
    } catch (error) {
      console.error('Error adding record:', error);
      alert('Failed to add record. Please check your data and try again.');
    }
  };

  const validateFormData = () => {
    const newErrors = {};

    if (!formData.horse_id && !formData.rfid_chip_number) {
      newErrors.horse_id = 'Please provide horse information.';
    }
    if (formData.procedures.length === 0)
      newErrors.procedures = 'At least one procedure must be selected.';
    if (!formData.payment_type_id)
      newErrors.payment_type_id = 'Payment type is required.';

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };
  const checkClientByPhone = async (phone) => {
    if (!phone || phone.length < 8) return;
    try {
      const response = await axios.get(`${apiUrl}/clients/by-phone/${phone}`);
      const data = response.data;

      if (data) {
        setFormData((prev) => ({
          ...prev,
          client_name: data.client_name,
          client_id: data.client_id,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          client_name: '',
          client_id: '',
        }));
      }
    } catch (error) {
      console.error('Error checking client:', error);
    }
  };

  const handleRFIDInputChange = (chipNumber) => {
    clearTimeout(chipSearchTimeoutRef.current);

    if (chipNumber.length >= 4) {
      chipSearchTimeoutRef.current = setTimeout(() => {
        fetchHorseByChipNumber(chipNumber);
      }, 800);
    }
  };

  return (
    <div>
      <Box display="flex" justifyContent="center" mt={3} mb={2}>
        <Typography variant="h4">Vet Clinic Record Form (Outside)</Typography>
      </Box>
      <TextField
        label="Date"
        type="datetime-local"
        name="record_date"
        value={formData.record_date}
        fullWidth
        margin="dense"
        disabled
      />

      <Box>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={11}>
            <TextField
              label="RFID Chip Number"
              name="rfid_chip_number"
              value={formData.rfid_chip_number}
              onChange={(e) => {
                handleChange(e);
                handleRFIDInputChange(e.target.value);
              }}
              fullWidth
              margin="dense"
            />
          </Grid>
        </Grid>
      </Box>
      <TextField
        label="Horse Name"
        name="horse_name"
        value={formData.horse_name}
        onChange={handleChange}
        fullWidth
        margin="dense"
      />
      <TextField
        label="Client Name"
        name="client_name"
        value={formData.client_name}
        onChange={handleChange}
        fullWidth
        margin="dense"
      />
      <TextField
        label="Client Phone"
        name="client_phone"
        value={formData.client_phone}
        onChange={(e) => {
          handleChange(e);
          checkClientByPhone(e.target.value); // سيتم شرحها بالأسفل
        }}
        fullWidth
        margin="dense"
      />

      <FormControl component="fieldset" margin="dense">
        <Typography>Procedures</Typography>
        <Grid container spacing={2}>
          {procedures.map((procedure) => (
            <Grid item xs={12} sm={6} md={4} key={procedure.procedure_id}>
              <FormControlLabel
                control={
                  <Checkbox
                    value={procedure.procedure_id}
                    checked={formData.procedures.includes(
                      procedure.procedure_id
                    )}
                    onChange={() =>
                      handleProcedureChange(procedure.procedure_id)
                    }
                  />
                }
                label={procedure.procedure_name}
              />
            </Grid>
          ))}
        </Grid>
        {errors.procedures && (
          <Typography color="error">{errors.procedures}</Typography>
        )}
      </FormControl>

      <FormControl
        component="fieldset"
        margin="dense"
        style={{ display: 'flex', flexWrap: 'wrap' }}
      >
        <Typography>Medications</Typography>
        {formData.medications.map((medication, index) => (
          <div key={index} style={{ marginBottom: '10px' }}>
            <Select
              name="medication_id"
              value={medication.medication_id}
              onChange={(e) =>
                handleMedicationChange(index, 'medication_id', e.target.value)
              }
              fullWidth
            >
              {medications.map((med) => (
                <MenuItem key={med.medication_id} value={med.medication_id}>
                  {med.medication_name}
                </MenuItem>
              ))}
            </Select>

            <TextField
              label="Dosage"
              name="dosage"
              value={medication.dosage}
              onChange={(e) =>
                handleMedicationChange(index, 'dosage', e.target.value)
              }
              fullWidth
              margin="dense"
            />
            <TextField
              label="Quantity"
              type="number"
              name="quantity"
              value={medication.quantity}
              onChange={(e) =>
                handleMedicationChange(index, 'quantity', e.target.value)
              }
              fullWidth
              margin="dense"
            />
            <TextField
              label="Unit"
              name="unit"
              value={medication.unit}
              onChange={(e) =>
                handleMedicationChange(index, 'unit', e.target.value)
              }
              fullWidth
              margin="dense"
            />
            <Button
              variant="contained"
              color="secondary"
              onClick={() => removeMedication(index)}
            >
              Remove Medication
            </Button>
          </div>
        ))}
        <Button variant="contained" color="primary" onClick={addMedication}>
          Add Medication
        </Button>
      </FormControl>
      <TextField
        label="Medical History"
        name="medical_history"
        value={formData.medical_history}
        onChange={handleChange}
        fullWidth
        margin="dense"
      />

      <TextField
        label="Diagnosis"
        name="diagnosis"
        value={formData.diagnosis}
        onChange={handleChange}
        fullWidth
        margin="dense"
      />

      <TextField
        label="Treatment"
        name="treatment"
        value={formData.treatment}
        onChange={handleChange}
        fullWidth
        margin="dense"
      />

      <TextField
        label="Notes"
        name="notes"
        value={formData.notes}
        onChange={handleChange}
        fullWidth
        margin="dense"
      />

      {false && (
        <>
          <TextField
            label="Total Amount"
            type="number"
            name="total_amount"
            value={formData.total_amount}
            onChange={handleChange}
            fullWidth
            margin="dense"
            disabled
          />
        </>
      )}

      <input type="file" multiple onChange={handleFileUpload} />
      <Box mt={2}>
        {formData.file_uploads.length > 0 && (
          <Typography variant="h6">Selected Files:</Typography>
        )}
        <ul>
          {formData.file_uploads.map((file, index) => (
            <li key={index}>{file}</li>
          ))}
        </ul>
      </Box>

      <FormControl fullWidth margin="dense" error={!!errors.payment_type_id}>
        <InputLabel>Payment Type</InputLabel>
        <Select
          name="payment_type_id"
          value={formData.payment_type_id}
          onChange={handleChange}
        >
          {paymentTypes.map((type) => (
            <MenuItem key={type.payment_type_id} value={type.payment_type_id}>
              {type.payment_type_name}
            </MenuItem>
          ))}
        </Select>
        {errors.payment_type_id && (
          <Typography color="error">{errors.payment_type_id}</Typography>
        )}
      </FormControl>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: 2,
        }}
      >
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          Submit
        </Button>
      </Box>
    </div>
  );
};

export default VetClinicRecordOutside;
